import { RightOutlined } from "@ant-design/icons";
import { Collapse, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { ExpiringLeasesResult } from "~/types/finance";
import {
  formatExpiringWindowLabel,
  getMaxExpiringWindowDays,
  hasExpiringLeases,
} from "~/utils/finance/expiringLeases";
import { EXPIRING_LEASES_BUCKET_HEADER_STYLES } from "./constants";
import { ExpiringLeasesCollapseLabel } from "./ExpiringLeasesCollapseLabel";
import { ExpiringLeasesTable } from "./ExpiringLeasesTable";

export type ExpiringLeasesSectionProps = {
  expiringLeases: ExpiringLeasesResult;
};

export const ExpiringLeasesSection = ({
  expiringLeases,
}: ExpiringLeasesSectionProps) => {
  const { t } = useTranslation();
  const emptyText = t("No_expiring_leases_records");
  const recordsLabel = t("Total_Records_Found");
  const maxWindowDays = getMaxExpiringWindowDays(expiringLeases);

  if (!hasExpiringLeases(expiringLeases)) {
    return (
      <Typography.Paragraph type="secondary">
        {t("No_expiring_leases")}
      </Typography.Paragraph>
    );
  }

  const windows = expiringLeases.buckets.map((bucket, index) => ({
    key: `within-${bucket.days}-days`,
    title: formatExpiringWindowLabel(bucket.days, t),
    items: bucket.items,
    styles:
      EXPIRING_LEASES_BUCKET_HEADER_STYLES[
        index % EXPIRING_LEASES_BUCKET_HEADER_STYLES.length
      ],
    rowKeyPrefix: `expiring-${bucket.days}d`,
  }));

  const collapseItems = windows.map((window) => ({
    key: window.key,
    label: (
      <ExpiringLeasesCollapseLabel
        title={window.title}
        count={window.items.length}
        recordsLabel={recordsLabel}
      />
    ),
    styles: window.styles,
    children: (
      <ExpiringLeasesTable
        items={window.items}
        rowKeyPrefix={window.rowKeyPrefix}
        emptyText={emptyText}
      />
    ),
  }));

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t("Expiring_leases_windows_hint", { days: maxWindowDays })}
      </Typography.Paragraph>
      <Collapse
        defaultActiveKey={[]}
        expandIcon={({ isActive }) => (
          <RightOutlined rotate={isActive ? 90 : 0} style={{ marginTop: 10 }} />
        )}
        items={collapseItems}
      />
    </Space>
  );
};
